<!--Copyright (c) Laserfiche.
Licensed under the MIT License. See LICENSE in the project root for license information.-->

<p class="documentation-title">Using UI Components in a Framework-Agnostic Way</p>

<h3 id="cdn-host-created-tags">Which tags you can create</h3>
<p class="documentation-text">
  The CDN bundle registers a custom element for each of the documented components. These are the tags a host is meant to
  create:
</p>
<ul>
  <li>
    <code>lf-field-container</code>, <code>lf-field-template-container</code>, <code>lf-field-adhoc-container</code>
  </li>
  <li><code>lf-repository-browser</code>, <code>lf-breadcrumbs</code>, <code>lf-toolbar</code></li>
  <li><code>lf-tags</code>, <code>lf-login</code>, <code>lf-checklist</code>, <code>lf-user-feedback</code></li>
</ul>
<p class="documentation-text">
  Other registered tags are internal building blocks. The library renders them itself under <code>lfint-</code>
  selectors, and they are not part of the supported surface — do not create them directly.
</p>
<p class="documentation-text">
  <code>lf-field-container</code> and <code>lf-repository-browser</code> are <em>composite</em>: they render other
  components inside their own view, and they are what most integrations embed. The live demo at the end of this page
  shows both, alongside the simpler leaf component taught below, and self-checks that each view rendered exactly once.
</p>

<h3 id="cdn-example-lf-tags">Simplest example: <code>lf-tags</code></h3>
<p>
  The steps below show how to use <code>lf-tags</code>, a leaf component, in a plain HTML file without any framework. A
  live demo is shown at the end.
</p>
<ol>
  <li>
    <p class="documentation-text">Import the UI components and CSS in your pages:</p>
    <ul>
      <li>Option 1: <a (click)="navigateToGettingStarted('cdn-usage')">Follow these steps to import from a CDN</a></li>
      <li>
        Option 2: Use npm to install <code>&#64;laserfiche/lf-ui-components</code> and copy the files from
        <code>./node_modules/&#64;laserfiche/lf-ui-components/cdn</code> to your distribution folder as a build step.
        See
        <a href="https://github.com/Laserfiche/laserfiche-sharepoint-integration" target="_blank"
          >laserfiche-sharepoint-integration</a
        >
        for an example using gulp.
      </li>
    </ul>
  </li>

  <li>
    <p class="documentation-text">Add the script and CSS to the <code>&lt;head&gt;</code> of your HTML file:</p>
    <pre class="code-container">
<span class="code-html-tagname">&lt;script</span> <span class="code-html-attribute-name">src</span><span>=</span><span class="code-html-attribute-value">"lf-ui-components.js"</span> <span class="code-html-attribute-name">defer</span><span class="code-html-tagname">&gt;&lt;/script&gt;</span>
<span class="code-html-tagname">&lt;link</span> <span class="code-html-attribute-name">rel</span><span>=</span><span class="code-html-attribute-value">"stylesheet"</span> <span class="code-html-attribute-name">href</span><span>=</span><span class="code-html-attribute-value">"indigo-pink.css"</span><span class="code-html-tagname">&gt;</span>
<span class="code-html-tagname">&lt;link</span> <span class="code-html-attribute-name">rel</span><span>=</span><span class="code-html-attribute-value">"stylesheet"</span> <span class="code-html-attribute-name">href</span><span>=</span><span class="code-html-attribute-value">"lf-laserfiche-lite.css"</span><span class="code-html-tagname">&gt;</span>
    </pre>
  </li>

  <li>
    <p dir="auto">Initialize the UI component in a <code>&lt;script&gt;</code> block in your HTML file:</p>
    <pre class="code-container">
  <span class="code-js-import-item">function</span> <span class="code-js-function">initLfTagsComponent</span>() &#123;
  <span class="code-js-import-item">const</span> <span class="code-js-prop">tagsElement</span> = document.<span class="code-js-function">createElement</span>('<span class="code-js-string">lf-tags</span>'); <span class="code-ts-comment">// create element before connecting to DOM</span>

  <span class="code-js-prop">tagsElement</span>.<span class="code-js-prop">tagsService</span> = tagsService; <span class="code-ts-comment">// required — must be set before appending</span>
  <span class="code-js-prop">tagsElement</span>.<span class="code-js-prop">initialTags</span> = [<span class="code-js-string">'Reviewed'</span>]; <span class="code-ts-comment">// optional — pre-select by displayName; must be set before appending</span>

  <span class="code-js-prop">tagsElement</span>.<span class="code-js-function">addEventListener</span>('<span class="code-js-string">selectedTagsChanged</span>', <span class="code-js-import-item">function</span>(event) &#123;
    <span class="code-js-import-item">const</span> <span class="code-js-prop">selectedTags</span> = event.detail; <span class="code-ts-comment">// LfTagDefinition[] — event data is in the detail property</span>
  &#125;);

  document.<span class="code-js-function">getElementById</span>(<span class="code-js-string">'container'</span>).<span class="code-js-function">appendChild</span>(<span class="code-js-prop">tagsElement</span>);
&#125;

customElements.<span class="code-js-function">whenDefined</span>('<span class="code-js-string">lf-tags</span>').<span class="code-js-function">then</span>(initLfTagsComponent); <span class="code-ts-comment">// wait for the element to be defined before creating and appending it</span>
    </pre>
  </li>
</ol>

<p class="documentation-sub-header">
  Live demo —
  <a href="./framework-agnostic-ui-component-demo.html" target="_blank">framework-agnostic-ui-component-demo.html</a>
</p>
<app-card fill>
  <iframe
    #demoFrame
    src="./framework-agnostic-ui-component-demo.html"
    class="demo-frame"
    (load)="onDemoFrameLoad(demoFrame)"
  ></iframe>
</app-card>
